import React, { useEffect, useRef, useState } from 'react';
import {
  Plus, Smile, Send, MessageSquareText, StickyNote, AlertTriangle,
  ChevronLeft, Search, Loader2, Boxes, Paperclip, Image as ImageIcon, FileText, X,
} from 'lucide-react';
import {
  whatsappInboxService, WhatsAppConversation, CatalogueProduct, MediaMessageType,
} from '@/services/integrations/whatsappInboxService';
import { sessionWindowInfo } from '@/utils/whatsappInbox';
import { displayName } from '@/utils/displayName';
import useDebounce from '@/hooks/useDebounce';
import TemplatePicker from './TemplatePicker';
import { TemplateSendPayload } from './TemplateVariableForm';

const PRODUCT_TEMPLATE_NAME = 'crm_product_card';

/**
 * Accepted types and size ceilings, mirroring MESSAGE_MEDIA_LIMITS in
 * meta_whatsapp_service.py. The server is the authority; checking here only
 * saves an agent from watching a 20MB upload finish before being told Meta
 * won't take it.
 */
const MEDIA_ACCEPT: Record<MediaMessageType, string> = {
  image: 'image/jpeg,image/png,image/webp',
  video: 'video/mp4,video/3gpp',
  audio: 'audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg',
  document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv',
};

const MEDIA_MAX_BYTES: Record<MediaMessageType, number> = {
  image: 5 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  audio: 16 * 1024 * 1024,
  document: 100 * 1024 * 1024,
};

/**
 * The clipboard hands over a mime type and never a file extension, so a pasted
 * attachment has to be classified by mime alone -- MEDIA_ACCEPT's document entry
 * is a list of extensions and is no use here. Kept in step with the same
 * MESSAGE_MEDIA_LIMITS table on the server.
 *
 * `text/plain` is deliberately absent: copied text must paste as text.
 */
const MIME_TO_MEDIA_TYPE: Record<string, MediaMessageType> = {
  'image/jpeg': 'image', 'image/png': 'image', 'image/webp': 'image',
  'video/mp4': 'video', 'video/3gpp': 'video',
  'audio/aac': 'audio', 'audio/mp4': 'audio', 'audio/mpeg': 'audio',
  'audio/amr': 'audio', 'audio/ogg': 'audio',
  'application/pdf': 'document',
  'application/msword': 'document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
  'application/vnd.ms-excel': 'document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'document',
  'application/vnd.ms-powerpoint': 'document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'document',
  'text/csv': 'document',
};

/** An attachment staged by a paste, waiting for the agent to confirm it. */
interface PendingPaste {
  file: File;
  mediaType: MediaMessageType;
  /** Object URL for the thumbnail; images only, revoked when it is replaced. */
  previewUrl: string | null;
}

const cheapestVariantPrice = (p: CatalogueProduct): { label: string } | null => {
  const prices = (p.variants || [])
    .map((v) => Number(v.price))
    .filter((n) => Number.isFinite(n));
  if (!prices.length) return null;
  const min = Math.min(...prices);
  const distinct = new Set(prices).size > 1;
  return { label: `${distinct ? 'From ' : ''}₹${min.toFixed(0)}` };
};

interface ComposerProps {
  conversation: WhatsAppConversation;
  currentUserName: string;
  onAfterSend: () => void;
  onAfterNote: () => void;
  /** Bump this number to force-open the template picker from outside
   * (e.g. the "Choose a template" CTA in WindowStatusBanner). */
  openTemplatePickerSignal?: number;
}

/**
 * Free-text / template-required / media composer (FE-13).
 *
 * "Optimistic send" here means the input clears and disables immediately
 * with a spinner, rather than fabricating a temporary message bubble —
 * the backend writes the outbound row synchronously before returning
 * (see BE-06/BE-11), so the real message appears within the same round
 * trip once `onAfterSend` triggers a thread refresh. A failed send instead
 * of vanishing shows an inline retry bar, satisfying the "retry where
 * technically possible" requirement (scope.txt §4) without the added
 * complexity of reconciling a fake message id against the real one.
 */
const Composer: React.FC<ComposerProps> = ({
  conversation: c, currentUserName, onAfterSend, onAfterNote, openTemplatePickerSignal,
}) => {
  const [text, setText] = useState('');
  const [noteMode, setNoteMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [pending, setPending] = useState<PendingPaste | null>(null);

  useEffect(() => {
    if (openTemplatePickerSignal !== undefined) setShowTemplatePicker(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTemplatePickerSignal]);
  // The + button is the product catalogue -- the quick-replies (abandoned cart,
  // order status, reorder nudge) and the payment link were removed from it, so
  // there is no intermediate one-row menu to show. Photo and document sending
  // lives on its own paperclip button rather than back under +, so + stays the
  // one-tap catalogue it was simplified into.
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingMediaType = useRef<MediaMessageType>('image');
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const attachBtnRef = useRef<HTMLButtonElement>(null);
  const productPickerRef = useRef<HTMLDivElement>(null);
  const productBtnRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [productQuery, setProductQuery] = useState('');
  const debouncedProductQuery = useDebounce(productQuery, 350);
  const [products, setProducts] = useState<CatalogueProduct[]>([]);
  const [productSearching, setProductSearching] = useState(false);
  const [sendingProductId, setSendingProductId] = useState<number | null>(null);

  const win = sessionWindowInfo(c.last_inbound_at);
  const canSendFreeform = win.open && !c.opted_out;

  useEffect(() => {
    if (!productPickerOpen) return;
    if (!debouncedProductQuery.trim()) { setProducts([]); return; }
    setProductSearching(true);
    whatsappInboxService.searchProducts(debouncedProductQuery)
      .then(setProducts)
      .finally(() => setProductSearching(false));
  }, [productPickerOpen, debouncedProductQuery]);

  const closeAttach = () => {
    setProductPickerOpen(false);
    setAttachMenuOpen(false);
    setProductQuery('');
    setProducts([]);
  };

  /**
   * Dismiss either popover on a click outside it, or on Escape.
   *
   * The trigger button has to count as "inside": it is what toggles the
   * popover, so treating it as outside would close on mousedown and let the
   * button's own click immediately reopen it — the menu could never be shut
   * with the button that opened it.
   */
  useEffect(() => {
    if (!attachMenuOpen && !productPickerOpen) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      const within = (el: HTMLElement | null) => !!el && el.contains(target);
      if (attachMenuOpen && !within(attachMenuRef.current) && !within(attachBtnRef.current)) {
        setAttachMenuOpen(false);
      }
      if (productPickerOpen && !within(productPickerRef.current) && !within(productBtnRef.current)) {
        closeAttach();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setAttachMenuOpen(false);
      if (productPickerOpen) closeAttach();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachMenuOpen, productPickerOpen]);

  const openFilePicker = (mediaType: MediaMessageType) => {
    pendingMediaType.current = mediaType;
    setAttachMenuOpen(false);
    const input = fileInputRef.current;
    if (!input) return;
    // Set `accept` on the element rather than rendering it from state: the
    // click has to happen in this same tick, before React could re-render
    // with a new accept value, so a rendered attribute would always be the
    // one for the *previous* choice — pick Document after Photo and you'd
    // get the image filter.
    input.accept = MEDIA_ACCEPT[mediaType];
    input.click();
  };

  /** Size-check, upload, refresh — shared by the paperclip picker and the clipboard. */
  const uploadAndSend = async (
    file: File, mediaType: MediaMessageType, caption?: string,
  ): Promise<boolean> => {
    const max = MEDIA_MAX_BYTES[mediaType];
    if (file.size > max) {
      setLastError(
        `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)}MB — WhatsApp's limit for a ${mediaType} is ${max / 1024 / 1024}MB`,
      );
      return false;
    }

    setSending(true);
    setLastError(null);
    try {
      const result = await whatsappInboxService.sendMedia(c.uuid, file, mediaType, caption);
      if (!result.ok) throw new Error(result.error || 'Attachment send failed');
      onAfterSend();
      return true;
    } catch (err: any) {
      setLastError(err?.message || 'Attachment send failed');
      return false;
    } finally {
      setSending(false);
    }
  };

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // so re-picking the same file fires change again
    if (!file) return;
    await uploadAndSend(file, pendingMediaType.current);
  };

  /**
   * Ctrl+V an attachment — the Snipping Tool case. Windows puts a bare bitmap on
   * the clipboard with no file behind it, so the paperclip's file dialog cannot
   * reach a screenshot at all; pasting is the only way in.
   *
   * The listener sits on `document` rather than on the textarea because the
   * agent has just come back from a screenshot tool and focus is nowhere in
   * particular — having to click into the box first is exactly what makes
   * pasting feel broken.
   *
   * Nothing is sent by the paste itself. It stages a preview, because a stray
   * Ctrl+V would otherwise fire an image at a customer with no undo.
   */
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (showTemplatePicker) return;      // the modal owns the clipboard while open
      const files = Array.from(e.clipboardData?.files || []);
      if (!files.length) return;           // plain text — let the browser paste it

      e.preventDefault();
      if (noteMode) {
        setLastError('An internal note is text only — turn Note off to send an attachment.');
        return;
      }
      if (!canSendFreeform) {
        setLastError(
          c.opted_out
            ? 'This customer has opted out — attachments cannot be sent.'
            : '24h window closed — an attachment needs an open window. Send an approved template first.',
        );
        return;
      }
      const file = files[0];
      const mediaType = MIME_TO_MEDIA_TYPE[(file.type || '').toLowerCase()];
      if (!mediaType) {
        setLastError(`WhatsApp cannot send ${file.type ? `a ${file.type}` : 'this kind of file'}.`);
        return;
      }
      setLastError(null);
      setPending({
        file, mediaType,
        previewUrl: mediaType === 'image' ? URL.createObjectURL(file) : null,
      });
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [showTemplatePicker, noteMode, canSendFreeform, c.opted_out]);

  // Release the thumbnail's object URL when it is replaced or the composer goes
  // away; this cleanup runs against the *previous* `pending`, which is the one
  // that owns the URL being dropped.
  useEffect(() => () => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
  }, [pending]);

  const sendPending = async () => {
    if (!pending || sending) return;
    // WhatsApp has no caption on an audio message; anywhere else whatever is
    // already typed rides along as the caption, as it does on WhatsApp Web.
    const caption = pending.mediaType === 'audio' ? undefined : text.trim() || undefined;
    const ok = await uploadAndSend(pending.file, pending.mediaType, caption);
    if (ok) {
      setText('');
      setPending(null);
    }
  };

  const handleSendProduct = async (p: CatalogueProduct) => {
    if (!p.image_url) {
      setLastError(`"${p.title}" has no image — can't send a product card without one`);
      return;
    }
    if (!p.handle) {
      setLastError(`"${p.title}" has no product page link on file yet`);
      return;
    }
    const price = cheapestVariantPrice(p);
    setSendingProductId(p.product_id);
    setLastError(null);
    try {
      const components = [
        { type: 'header', parameters: [{ type: 'image', image: { link: p.image_url } }] },
        {
          type: 'body',
          parameters: [
            { type: 'text', text: p.title },
            { type: 'text', text: price?.label || 'See website' },
            { type: 'text', text: p.description || 'Tap below for full details.' },
          ],
        },
        { type: 'button', sub_type: 'url', index: '0', parameters: [{ type: 'text', text: p.handle }] },
      ];
      const result = await whatsappInboxService.sendTemplate(c.uuid, {
        template_name: PRODUCT_TEMPLATE_NAME, language: 'en', components,
      });
      if (!result.ok) throw new Error(result.error || 'Product card send failed');
      closeAttach();
      onAfterSend();
    } catch (e: any) {
      setLastError(e?.message || 'Product card send failed');
    } finally {
      setSendingProductId(null);
    }
  };

  const doSend = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    setLastError(null);
    setText('');
    try {
      if (noteMode) {
        const note = await whatsappInboxService.addNote(c.uuid, body);
        if (!note) throw new Error('Failed to add note');
        onAfterNote();
      } else {
        const result = await whatsappInboxService.sendText(c.uuid, body);
        if (!result.ok) throw new Error(result.error || 'Send failed');
        onAfterSend();
      }
    } catch (e: any) {
      setLastError(e?.message || 'Send failed');
      setText(body); // put it back so the agent doesn't retype
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // With an attachment staged, Enter sends it (with whatever is typed as the
      // caption) rather than sending the caption on its own as a second message.
      if (pending) sendPending(); else doSend();
    }
  };

  // Escape discards a staged attachment, wherever focus happens to be.
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending) setPending(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pending, sending]);

  // Grow the box with its content: `rows={1}` on its own means a pasted
  // multi-line message shows one line of itself with the rest out of sight.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [text]);

  const handleTemplateSend = async (payload: TemplateSendPayload) => {
    setLastError(null);
    const result = await whatsappInboxService.sendTemplate(c.uuid, payload);
    if (!result.ok) {
      setLastError(result.error || 'Template send failed');
      return;
    }
    setShowTemplatePicker(false);
    onAfterSend();
  };

  return (
    <div className="relative bg-white border-t border-gray-200 px-5 pt-3 pb-3.5">
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChosen} />

      {attachMenuOpen && (
        <div
          ref={attachMenuRef}
          className="absolute bottom-[78px] left-[62px] w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden"
        >
          <button
            onClick={() => openFilePicker('image')}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-gray-50 border-b border-gray-100"
          >
            <span className="w-8 h-8 rounded-lg bg-[#eef5f0] flex items-center justify-center flex-shrink-0">
              <ImageIcon className="w-4 h-4 text-[#153d23]" />
            </span>
            <span className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-gray-900">Photo</div>
              <div className="text-[11px] text-gray-500">JPG, PNG or WebP · up to 5MB</div>
            </span>
          </button>
          <button
            onClick={() => openFilePicker('document')}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-gray-50"
          >
            <span className="w-8 h-8 rounded-lg bg-[#eef5f0] flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-[#153d23]" />
            </span>
            <span className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-gray-900">Document</div>
              <div className="text-[11px] text-gray-500">PDF, Office or CSV · up to 100MB</div>
            </span>
          </button>
        </div>
      )}

      {productPickerOpen && (
        <div
          ref={productPickerRef}
          className="absolute bottom-[78px] left-5 w-80 max-h-[70vh] flex flex-col bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden"
        >
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
            <button onClick={() => setProductPickerOpen(false)} className="text-gray-500 hover:text-gray-700">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex-1 flex items-center gap-1.5 h-8 border border-gray-200 rounded-lg px-2 bg-gray-50">
              <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <input
                autoFocus
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
                placeholder="Search products…"
                className="flex-1 text-[12.5px] bg-transparent border-0 outline-none placeholder:text-gray-400 min-w-0"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {productSearching ? (
              <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
            ) : !productQuery.trim() ? (
              <div className="py-8 text-center text-[12px] text-gray-400">Start typing a product name…</div>
            ) : products.length === 0 ? (
              <div className="py-8 text-center text-[12px] text-gray-400">No products match.</div>
            ) : (
              products.map((p) => {
                const price = cheapestVariantPrice(p);
                const isSending = sendingProductId === p.product_id;
                return (
                  <button
                    key={p.product_id}
                    onClick={() => handleSendProduct(p)}
                    disabled={sendingProductId !== null}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-gray-50 border-b border-gray-100 disabled:opacity-50"
                  >
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <span className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Boxes className="w-4 h-4 text-gray-400" />
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-gray-900 truncate">{p.title}</div>
                      <div className="text-[11px] text-gray-500">{price?.label || 'Price on website'}</div>
                    </span>
                    {isSending && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {pending && (
        <div className="flex items-center gap-3 mb-2 px-3 py-2.5 rounded-xl bg-[#f6faf7] border border-[#d9e8e0]">
          {pending.previewUrl ? (
            <img
              src={pending.previewUrl}
              alt=""
              className="w-12 h-12 rounded-lg object-cover border border-[#d9e8e0] flex-shrink-0"
            />
          ) : (
            <span className="w-12 h-12 rounded-lg bg-white border border-[#d9e8e0] flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-[#153d23]" />
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-semibold text-gray-900 truncate">
              {pending.file.name || `Pasted ${pending.mediaType}`}
            </div>
            <div className="text-[11px] text-gray-500">
              {(pending.file.size / 1024).toFixed(0)} KB · pasted from clipboard
              {pending.mediaType !== 'audio' && ' · type a caption below, or just send'}
            </div>
          </div>
          <button
            onClick={sendPending}
            disabled={sending}
            className="h-8 px-3 rounded-lg bg-[#153d23] text-white text-[12px] font-bold flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 flex-shrink-0"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send
          </button>
          <button
            onClick={() => setPending(null)}
            disabled={sending}
            title="Discard (Esc)"
            className="w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-500 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {lastError && (
        <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[12px]">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="flex-1">{lastError}</span>
          <button onClick={doSend} className="font-bold underline flex-shrink-0">Retry</button>
          <button onClick={() => setLastError(null)} className="flex-shrink-0">✕</button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <button
          ref={productBtnRef}
          onClick={() => {
            if (productPickerOpen) { closeAttach(); return; }
            setAttachMenuOpen(false);
            setProductPickerOpen(true);
          }}
          disabled={!canSendFreeform}
          title="Send a product from the catalogue"
          className="w-10 h-10 flex-shrink-0 rounded-[10px] border border-gray-300 bg-white text-gray-700 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40"
        >
          <Plus className="w-[19px] h-[19px]" />
        </button>
        <button
          ref={attachBtnRef}
          onClick={() => {
            setProductPickerOpen(false);
            setAttachMenuOpen((v) => !v);
          }}
          disabled={!canSendFreeform || sending}
          title={canSendFreeform ? 'Send a photo or document' : '24h window closed — attachments need an open window'}
          className={`w-10 h-10 flex-shrink-0 rounded-[10px] border flex items-center justify-center disabled:opacity-40 ${
            attachMenuOpen
              ? 'bg-[#eef5f0] border-[#153d23] text-[#153d23]'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Paperclip className="w-[18px] h-[18px]" />
        </button>
        <button
          disabled
          className="w-10 h-10 flex-shrink-0 rounded-[10px] border border-gray-300 bg-white text-gray-400 flex items-center justify-center"
          title="Emoji — coming soon"
        >
          <Smile className="w-[19px] h-[19px]" />
        </button>

        <div
          className={`flex-1 border rounded-[11px] px-3.5 py-2.5 min-h-[40px] ${
            noteMode ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-300'
          }`}
        >
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending || (!canSendFreeform && !noteMode)}
            rows={1}
            placeholder={
              pending ? 'Add a caption… (optional)'
                : noteMode ? 'Internal note — never sent to WhatsApp…'
                : canSendFreeform ? `Message ${displayName(c.lead_name) || 'customer'}…`
                : '24h window closed — send an approved template'
            }
            className="w-full resize-none overflow-y-auto bg-transparent border-0 outline-none text-[13.5px] placeholder:text-gray-400 disabled:cursor-not-allowed"
          />
        </div>

        <button
          onClick={() => setShowTemplatePicker(true)}
          className="h-10 px-3.5 rounded-[10px] border border-gray-300 bg-white text-[12.5px] font-bold text-gray-700 flex items-center gap-1.5 hover:bg-gray-50 flex-shrink-0"
        >
          <MessageSquareText className="w-4 h-4 text-purple-600" /> Templates
        </button>
        <button
          onClick={() => setNoteMode((v) => !v)}
          className={`h-10 px-3 rounded-[10px] border text-[12.5px] font-bold flex-shrink-0 ${
            noteMode ? 'bg-amber-100 border-amber-300 text-amber-900' : 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
          }`}
        >
          <StickyNote className="w-4 h-4 inline mr-1" /> Note
        </button>
        <button
          onClick={() => (pending ? sendPending() : doSend())}
          disabled={sending || (!pending && !text.trim()) || (!canSendFreeform && !noteMode)}
          className="w-11 h-10 flex-shrink-0 rounded-[10px] bg-[#153d23] border border-[#153d23] text-white flex items-center justify-center hover:opacity-90 disabled:opacity-40"
        >
          <Send className="w-[18px] h-[18px]" />
        </button>
      </div>

      <div className="flex items-center gap-3.5 mt-2 pl-0.5">
        <div className="text-[11px] text-gray-500">
          Sending as <strong className="text-gray-700">{currentUserName}</strong>
        </div>
        <div className="text-[11px] text-gray-400">
          Enter to send · Shift+Enter new line · Ctrl+V to paste a screenshot
        </div>
      </div>

      {showTemplatePicker && (
        <TemplatePicker onClose={() => setShowTemplatePicker(false)} onSend={handleTemplateSend} />
      )}
    </div>
  );
};

export default Composer;
